import {
  AnalyticsEvents,
  AvatarUpdatedAvatarAction,
  AvatarUpdatedProperties,
  NameUpdatedProperties,
} from '@mondaygpt/analytics-events'
import { createAsyncThunk, createEntityAdapter, createSlice } from '@reduxjs/toolkit'
import { selectCurrentUserId } from 'modules/common'
import { apiCall } from 'modules/core'
import { normalize } from 'normalizr'
import { UpdateUserDto, UserEntity, UsersService } from 'openapi'
import { intercom } from 'services/intercom'
import { analytics } from 'services/mixpanel/analytics-config'
import { userSchema } from 'store/schema'
import { RootState } from 'types'

import { fbInit } from '../../../services/facebook-pixel'
import { funnelFighters, readAttributionCookie } from '../../../services/funnel-fighters'
import { NormalizedUserEntity, PlainUserEntity } from '../entities'
import { fetchCurrentUserAction, logout } from './authentication-view.slice'

const { usersControllerUpdateV1 } = UsersService

export const updateCurrentUser = createAsyncThunk(
  'users/update',
  async (body: UpdateUserDto, { rejectWithValue, getState }) => {
    // Get current user data before update for comparison
    const state = getState() as RootState
    const currentUserId = selectCurrentUserId(state)
    const currentUser = currentUserId ? selectUserById(state, currentUserId) : null

    const result = await apiCall(usersControllerUpdateV1, rejectWithValue, body)

    // Return both the result and the previous user data for analytics
    return { updatedUser: result, previousUser: currentUser, updateData: body }
  }
)

// Entity adapter
const entityAdapter = createEntityAdapter<PlainUserEntity>({
  sortComparer: (entityA, entityB) => (entityA.name || '').localeCompare(entityB.name || ''),
})

const initialState = entityAdapter.getInitialState()

// Define the slice
const slice = createSlice({
  name: 'userEntities',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(logout, () => initialState)

    builder.addCase(updateCurrentUser.fulfilled, (state, action) => {
      const { updatedUser, previousUser, updateData } = action.payload

      const normalizedData = normalize<UserEntity, { users: Record<string, NormalizedUserEntity> }>(
        updatedUser,
        userSchema
      )

      const [nextEntity] = Object.values(normalizedData.entities.users || [])
      entityAdapter.upsertOne(state, nextEntity)

      // Track specific profile update events and update user properties in analytics
      try {
        // Track name update if name was changed
        if (updateData.name && previousUser && previousUser.name !== updatedUser.name) {
          analytics.track(AnalyticsEvents.Profile.NAME_UPDATED, {
            user_id: updatedUser.id,
            previous_name: previousUser.name,
            new_name: updatedUser.name,
          } satisfies NameUpdatedProperties)
        }

        // Track avatar update only if avatar actually changed
        if (
          'avatarId' in updateData &&
          previousUser &&
          previousUser.avatar?.id !== updatedUser.avatar?.id
        ) {
          const hadBefore = !!previousUser.avatar
          const hasNow = !!updatedUser.avatar

          analytics.track(AnalyticsEvents.Profile.AVATAR_UPDATED, {
            user_id: updatedUser.id,
            had_avatar_before: hadBefore,
            avatar_action:
              !hadBefore && hasNow
                ? AvatarUpdatedAvatarAction.ADDED
                : hadBefore && !hasNow
                  ? AvatarUpdatedAvatarAction.REMOVED
                  : AvatarUpdatedAvatarAction.UPDATED,
          } satisfies AvatarUpdatedProperties)
        }

        // Update user properties in analytics using Mixpanel official fields
        analytics.setUserProperties({
          $name: updatedUser.name,
          $email: updatedUser.email,
          user_role: updatedUser.role,
          onboarding_version: updatedUser.onboardingData.completedVersion || 0,
          profile_updated_at: new Date().toISOString(),
        })

        fbInit({
          email: updatedUser.email,
          name: updatedUser.name,
        })

        // Update Intercom with updated user data
        intercom.updateUser(updatedUser)
      } catch (error) {
        console.warn('Failed to track profile update in analytics:', error)
      }
    })

    builder.addCase(fetchCurrentUserAction.fulfilled, (state, action) => {
      const normalizedData = normalize<UserEntity, { users: Record<string, NormalizedUserEntity> }>(
        action.payload,
        [userSchema]
      )

      const nextEntities = Object.values(normalizedData.entities.users || [])

      const currentUser = nextEntities[0]
      analytics.identify(currentUser.id)
      analytics.setUserProperties({
        $name: currentUser.name,
        $email: currentUser.email,
      })

      fbInit({
        email: currentUser.email,
        name: currentUser.name,
      })

      // Wrapped: a throw here would skip intercom.init AND entityAdapter.upsertMany
      // below, leaving Redux without the current user. Match intercom's pattern.
      try {
        funnelFighters.identify(currentUser.id, {
          email: currentUser.email,
          name: currentUser.name,
          signup_date: currentUser.createdAt,
          ...readAttributionCookie(),
        })
      } catch {
        // Fire-and-forget analytics; never block auth.
      }

      // Initialize Intercom with user data
      try {
        intercom.init(currentUser)
      } catch (error) {
        console.warn('Failed to update Intercom user:', error)
      }

      entityAdapter.upsertMany(state, nextEntities)
    })
  },
})

export const userEntitiesReducer = slice.reducer

// Export the standard reducers and the reducer function
const { selectEntities: selectUserEntities, selectById: selectUserById } =
  entityAdapter.getSelectors((state: RootState) => state.userEntities)

export { selectUserById, selectUserEntities }
